<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/modals/FileValidationModal.vue -->

<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  validationResult: {
    type: Object,
    default: () => ({
      isValid: true,
      errors: [],
      warnings: [],
      fileSize: 0,
      fileSizeMB: 0,
      formattedSize: '',
      shouldUseOptimized: false,
    }),
  },
  fileName: {
    type: String,
    default: '',
  },
  estimatedTime: {
    type: Object,
    default: () => ({ seconds: 0, formatted: '' }),
  },
  recommendations: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'continue', 'cancel']);

const hasErrors = computed(() => {
  return props.validationResult.errors && props.validationResult.errors.length > 0;
});

const hasWarnings = computed(() => {
  return props.validationResult.warnings && props.validationResult.warnings.length > 0;
});

const hasLargeFileWarning = computed(() => {
  return props.validationResult.warnings.some(w => 
    w.type === 'LARGE_FILE_WARNING' || w.type === 'OPTIMIZED_PROCESSING'
  );
});

const modalTitle = computed(() => {
  if (hasErrors.value) {
    return 'Erro na Validação do Arquivo';
  } else if (hasLargeFileWarning.value) {
    return 'Arquivo Grande Detectado';
  } else {
    return 'Validação do Arquivo';
  }
});

const modalIcon = computed(() => {
  if (hasErrors.value) {
    return 'i-lucide-alert-circle';
  } else if (hasLargeFileWarning.value) {
    return 'i-lucide-alert-triangle';
  } else {
    return 'i-lucide-info';
  }
});

const modalIconColor = computed(() => {
  if (hasErrors.value) {
    return 'from-n-ruby-9 to-n-ruby-10';
  } else if (hasLargeFileWarning.value) {
    return 'from-n-amber-9 to-n-amber-10';
  } else {
    return 'from-n-iris-9 to-n-iris-10';
  }
});

const formattedFileName = computed(() => {
  if (!props.fileName) return '';
  if (props.fileName.length > 40) {
    return props.fileName.substring(0, 37) + '...';
  }
  return props.fileName;
});

const canContinue = computed(() => {
  return !hasErrors.value;
});

const handleContinue = () => {
  emit('continue');
};

const handleCancel = () => {
  emit('cancel');
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-2xl max-h-screen overflow-hidden bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
    >
      <!-- Header -->
      <div
        class="flex justify-between items-center px-5 py-4 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center shadow-md"
            :class="modalIconColor"
          >
            <i :class="modalIcon" class="text-white text-base" />
          </div>
          <div>
            <h3 class="text-xl font-bold text-n-slate-12 dark:text-n-slate-12">
              {{ modalTitle }}
            </h3>
            <p class="text-sm text-n-slate-11 dark:text-n-slate-11 font-medium">
              Verificação antes da importação
            </p>
          </div>
        </div>
        <button
          class="text-n-slate-11 dark:text-n-slate-11 p-2 hover:bg-n-slate-3 dark:hover:bg-n-slate-3 hover:text-n-slate-12 dark:hover:text-n-slate-12 rounded-lg transition-all duration-200"
          @click="handleClose"
        >
          <i class="i-lucide-x text-lg" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <!-- File Info -->
        <div
          class="flex items-center gap-3 p-3 bg-n-slate-2 dark:bg-n-slate-2 rounded-lg"
        >
          <div
            class="w-10 h-10 bg-n-iris-3 dark:bg-n-iris-3 rounded-lg flex items-center justify-center"
          >
            <i class="i-lucide-file text-n-iris-11 text-base" />
          </div>
          <div class="flex-1 min-w-0">
            <p
              class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 truncate"
              :title="fileName"
            >
              {{ formattedFileName }}
            </p>
            <p class="text-xs text-n-slate-11 dark:text-n-slate-11">
              {{ validationResult.formattedSize }} • Arquivo CSV/Excel
            </p>
          </div>
        </div>

        <!-- Errors Section -->
        <div
          v-if="hasErrors"
          class="space-y-3"
        >
          <div
            v-for="error in validationResult.errors"
            :key="error.type"
            class="p-4 bg-gradient-to-r from-n-ruby-2 to-n-ruby-3 dark:from-n-ruby-2 dark:to-n-ruby-3 border border-n-ruby-6 dark:border-n-ruby-6 rounded-lg"
          >
            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 bg-gradient-to-br from-n-ruby-9 to-n-ruby-10 dark:from-n-ruby-9 dark:to-n-ruby-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0"
              >
                <i class="i-lucide-x text-white text-sm" />
              </div>
              <div>
                <h4
                  class="text-base font-bold text-n-ruby-12 dark:text-n-ruby-12 mb-2"
                >
                  Erro
                </h4>
                <p class="text-sm text-n-ruby-11 dark:text-n-ruby-11">
                  {{ error.message }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Warnings Section -->
        <div
          v-if="hasWarnings"
          class="space-y-3"
        >
          <div
            v-for="warning in validationResult.warnings"
            :key="warning.type"
            class="p-4 bg-gradient-to-r from-n-amber-2 to-n-amber-3 dark:from-n-amber-2 dark:to-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded-lg"
          >
            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 bg-gradient-to-br from-n-amber-9 to-n-amber-10 dark:from-n-amber-9 dark:to-n-amber-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0"
              >
                <i class="i-lucide-alert-triangle text-white text-sm" />
              </div>
              <div class="flex-1">
                <h4
                  class="text-base font-bold text-n-amber-12 dark:text-n-amber-12 mb-2"
                >
                  {{ warning.type === 'OPTIMIZED_PROCESSING' ? 'Processamento Otimizado' : 'Atenção' }}
                </h4>
                <p class="text-sm text-n-amber-11 dark:text-n-amber-11 mb-3">
                  {{ warning.message }}
                </p>
                
                <!-- Show features for optimized processing -->
                <div
                  v-if="warning.features && warning.features.length > 0"
                  class="space-y-1"
                >
                  <p class="text-xs font-semibold text-n-amber-12 dark:text-n-amber-12">
                    Funcionalidades especiais:
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="feature in warning.features"
                      :key="feature"
                      class="flex items-center gap-2 text-xs text-n-amber-11 dark:text-n-amber-11"
                    >
                      <i class="i-lucide-check text-xs text-n-teal-9" />
                      {{ feature }}
                    </li>
                  </ul>
                </div>

                <!-- Show recommendations for large files -->
                <div
                  v-if="warning.recommendations && warning.recommendations.length > 0"
                  class="space-y-1 mt-3"
                >
                  <p class="text-xs font-semibold text-n-amber-12 dark:text-n-amber-12">
                    Recomendações:
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="recommendation in warning.recommendations"
                      :key="recommendation"
                      class="flex items-start gap-2 text-xs text-n-amber-11 dark:text-n-amber-11"
                    >
                      <i class="i-lucide-lightbulb text-xs text-n-amber-9 mt-0.5 flex-shrink-0" />
                      {{ recommendation }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Processing Time Estimate -->
        <div
          v-if="estimatedTime.seconds > 0 && canContinue"
          class="p-4 bg-gradient-to-r from-n-iris-2 to-n-iris-3 dark:from-n-iris-2 dark:to-n-iris-3 border border-n-iris-6 dark:border-n-iris-6 rounded-lg"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-n-iris-9 to-n-iris-10 dark:from-n-iris-9 dark:to-n-iris-10 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-clock text-white text-sm" />
            </div>
            <div>
              <h4
                class="text-base font-bold text-n-iris-12 dark:text-n-iris-12 mb-2"
              >
                Tempo Estimado de Processamento
              </h4>
              <p class="text-sm text-n-iris-11 dark:text-n-iris-11">
                Aproximadamente <strong>{{ estimatedTime.formatted }}</strong> para processar este arquivo.
              </p>
            </div>
          </div>
        </div>

        <!-- Performance Recommendations -->
        <div
          v-if="recommendations.length > 0 && canContinue"
          class="p-4 bg-gradient-to-r from-n-teal-2 to-n-teal-3 dark:from-n-teal-2 dark:to-n-teal-3 border border-n-teal-6 dark:border-n-teal-6 rounded-lg"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-n-teal-9 to-n-teal-10 dark:from-n-teal-9 dark:to-n-teal-10 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-zap text-white text-sm" />
            </div>
            <div>
              <h4
                class="text-base font-bold text-n-teal-12 dark:text-n-teal-12 mb-2"
              >
                Dicas para Melhor Performance
              </h4>
              <ul class="space-y-1">
                <li
                  v-for="recommendation in recommendations"
                  :key="recommendation"
                  class="flex items-start gap-2 text-sm text-n-teal-11 dark:text-n-teal-11"
                >
                  <i class="i-lucide-arrow-right text-xs text-n-teal-9 mt-1 flex-shrink-0" />
                  {{ recommendation }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Success Message for Valid Files -->
        <div
          v-if="!hasErrors && !hasWarnings"
          class="p-4 bg-gradient-to-r from-n-teal-2 to-n-teal-3 dark:from-n-teal-2 dark:to-n-teal-3 border border-n-teal-6 dark:border-n-teal-6 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-n-teal-9 to-n-teal-10 dark:from-n-teal-9 dark:to-n-teal-10 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-check text-white text-sm" />
            </div>
            <div>
              <h4
                class="text-base font-bold text-n-teal-12 dark:text-n-teal-12 mb-1"
              >
                Arquivo Válido
              </h4>
              <p class="text-sm text-n-teal-11 dark:text-n-teal-11">
                Seu arquivo passou em todas as validações e está pronto para ser processado.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex justify-between items-center px-5 py-4 border-t border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-b-xl"
      >
        <Button
          label="Cancelar"
          variant="outline"
          color="slate"
          class="transition-all duration-200 hover:scale-105"
          @click="handleCancel"
        />

        <div class="flex gap-3">
          <Button
            v-if="canContinue"
            :label="hasWarnings ? 'Prosseguir Mesmo Assim' : 'Continuar'"
            :color="hasWarnings ? 'amber' : 'primary'"
            class="transition-all duration-200 hover:scale-105"
            @click="handleContinue"
          />
          <Button
            v-else
            label="Escolher Outro Arquivo"
            color="primary"
            class="transition-all duration-200 hover:scale-105"
            @click="handleCancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>